export const SETTINGS_LOCATION_EVENT = "seexr:settings-location";

export const SETTINGS_TABS = [
  "general",
  "profile",
  "instructions",
  "plan",
  "usage",
  "storage",
  "security",
  "devices",
] as const;

export type SettingsTab = (typeof SETTINGS_TABS)[number];

export const PLAN_CATALOG_SETTINGS_HASH = "#settings/plan/catalog";

type SettingsHistoryState = {
  baseUrl: string;
  depth: number;
  direct: boolean;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function currentBaseUrl(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function notifySettingsLocationChange(): void {
  window.dispatchEvent(new Event(SETTINGS_LOCATION_EVENT));
}

function currentSettingsState(): SettingsHistoryState | null {
  const state = window.history.state;
  if (!isRecord(state) || !isRecord(state.seexrSettings)) return null;

  const settings = state.seexrSettings;
  if (
    typeof settings.baseUrl !== "string" ||
    typeof settings.depth !== "number" ||
    typeof settings.direct !== "boolean"
  ) {
    return null;
  }

  return {
    baseUrl: settings.baseUrl,
    depth: settings.depth,
    direct: settings.direct,
  };
}

function withSettingsState(
  settings: SettingsHistoryState,
): Record<string, unknown> {
  const current = isRecord(window.history.state) ? window.history.state : {};
  return { ...current, seexrSettings: settings };
}

function withoutSettingsState(): Record<string, unknown> {
  const current = isRecord(window.history.state) ? window.history.state : {};
  const rest = { ...current };
  delete rest.seexrSettings;
  return rest;
}

export function settingsHashFor(tab: SettingsTab): string {
  return tab === "general" ? "#settings" : `#settings/${tab}`;
}

export function isPlanCatalogSettingsHash(hash: string): boolean {
  const normalized = hash.trim().toLowerCase().replace(/\/$/, "");
  return normalized === PLAN_CATALOG_SETTINGS_HASH;
}

export function parseSettingsHash(hash: string): SettingsTab | null {
  const normalized = hash.trim().toLowerCase();
  if (normalized === "#settings" || normalized === "#settings/")
    return "general";
  if (!normalized.startsWith("#settings/")) return null;

  const segment = normalized.slice("#settings/".length).split("/")[0];
  return SETTINGS_TABS.includes(segment as SettingsTab)
    ? (segment as SettingsTab)
    : "general";
}

export function ensureSettingsHistoryState(): void {
  if (
    parseSettingsHash(window.location.hash) === null ||
    currentSettingsState()
  )
    return;

  window.history.replaceState(
    withSettingsState({
      baseUrl: currentBaseUrl(),
      depth: 0,
      direct: true,
    }),
    "",
    window.location.href,
  );
}

export function canonicalizeSettingsHash(tab: SettingsTab): void {
  if (tab === "plan" && isPlanCatalogSettingsHash(window.location.hash)) {
    return;
  }
  const canonicalHash = settingsHashFor(tab);
  if (window.location.hash === canonicalHash) return;

  window.history.replaceState(
    window.history.state,
    "",
    `${currentBaseUrl()}${canonicalHash}`,
  );
  notifySettingsLocationChange();
}

function openSettingsHash(hash: string): void {
  if (window.location.hash === hash) {
    ensureSettingsHistoryState();
    notifySettingsLocationChange();
    return;
  }

  const existing = currentSettingsState();
  const baseUrl = currentBaseUrl();
  const nextState: SettingsHistoryState =
    parseSettingsHash(window.location.hash) !== null
      ? {
          baseUrl: existing?.baseUrl ?? baseUrl,
          depth: (existing?.depth ?? 0) + 1,
          direct: existing?.direct ?? true,
        }
      : {
          baseUrl,
          depth: 1,
          direct: false,
        };

  window.history.pushState(
    withSettingsState(nextState),
    "",
    `${baseUrl}${hash}`,
  );
  notifySettingsLocationChange();
}

export function openSettings(tab: SettingsTab = "general"): void {
  openSettingsHash(settingsHashFor(tab));
}

export function openPlanCatalog(): void {
  openSettingsHash(PLAN_CATALOG_SETTINGS_HASH);
}

export function returnToPlanSettings(): void {
  window.history.replaceState(
    window.history.state,
    "",
    `${currentBaseUrl()}${settingsHashFor("plan")}`,
  );
  notifySettingsLocationChange();
}

export function closeSettings(): void {
  const settings = currentSettingsState();
  const baseUrl = settings?.baseUrl ?? currentBaseUrl();

  if (settings && !settings.direct && settings.depth > 0) {
    window.history.go(-settings.depth);
    return;
  }

  if (settings?.direct && settings.depth > 0) {
    const finishClose = () => {
      window.history.replaceState(withoutSettingsState(), "", baseUrl);
      notifySettingsLocationChange();
    };
    window.addEventListener("popstate", finishClose, { once: true });
    window.history.go(-settings.depth);
    return;
  }

  window.history.replaceState(withoutSettingsState(), "", baseUrl);
  notifySettingsLocationChange();
}
